import { FrameworkId } from "./frameworkId";
import { FrameworkTargetCategory } from "./list";
const ALL_MINIMUM_EXPECTATIONS = "All Minimum Expectations";
const CLIMATE_POSITIVE = "Climate Positive Pathway";
/**
 * Whether one level may count several sources of a kind, summed. RAP
 * deliverables count "across all relevant RAPs" (Buildings v1.1 p.262), and a
 * client's RAP is adopted beside the head contractor's; a register is one
 * population of workers, so one per level.
 */
export const coverageKindTakesSeveral = {
    adoptedFramework: true,
    employeeRegister: false
};
export const isCreditCoverageKind = (value) => typeof value === "string" &&
    Object.keys(coverageKindTakesSeveral).includes(value);
const greenStarLevels = [
    { id: "minimumExpectation", label: "Minimum Expectation", shortLabel: "ME" },
    { id: "credit", label: "Credit Achievement", shortLabel: "Credit" },
    {
        id: "exceptional",
        label: "Exceptional Performance",
        shortLabel: "Exceptional"
    }
];
const REVIEWED_ON = "2026-09-21";
const GSB_GUIDELINES = "GBCA Green Star Buildings v1.1 Submission Guidelines, Rev A (17 Dec 2025)";
const NSW_SCORECARD = "Green Star scorecard lodged on the NSW Major Projects portal (SSD-79146716), and Sustainable Development Plan P5-2024-218";
const GSB1_GUIDELINES = "GBCA Green Star Buildings Submission Guidelines v1, Rev C (18 Oct 2023)";
const GSC_GUIDELINES = "GBCA Green Star Communities v2 Submission Guidelines (27 Feb 2025)";
const GSF_GUIDELINES = "GBCA Green Star Fitouts v1 Submission Guidelines (18 Mar 2026)";
const DAB_GUIDELINES = "GBCA Green Star Design & As Built v1.2 Submission Guidelines (28 Jun 2017)";
const SUBSTITUTION_GUIDE = "GBCA, Green Star Buildings: guidance on credit substitutions (Release 3, 30 Jun 2022)";
// `RatingScaleStep.requires`. Pathways tighten by registration year, which no
// project record holds, so the year is said, not applied. A condition starting
// in a future year is left off: Buildings v1.1's Nature Positive Pathway starts
// with 2028 registrations (p.22); add it back at the 2028 review.
const GSB_HIGHER_MINIMUM_EXPECTATIONS = "All Minimum Expectations, including those for 5 and 6 Star";
const GSB_PATHWAYS = [`${CLIMATE_POSITIVE} (criteria by registration year)`];
const GSB_STAR_REQUIRES = [ALL_MINIMUM_EXPECTATIONS, ...GSB_PATHWAYS];
const GSB_HIGHER_STAR_REQUIRES = [
    GSB_HIGHER_MINIMUM_EXPECTATIONS,
    ...GSB_PATHWAYS
];
// Communities v2 p.12 names both pathways for every star, but its tables phase
// Nature Positive in from 2027 (5 and 6 Star) and 2030 (4 Star) (p.20), so it
// is left off until 2027 registrations exist.
const GSC_STAR_REQUIRES = [
    ALL_MINIMUM_EXPECTATIONS,
    `${CLIMATE_POSITIVE} (criteria by registration year)`
];
const DAB_CONDITIONAL_REQUIREMENTS = "Conditional Requirements of credits 15 and 24";
export const frameworkCreditScopes = [
    {
        credits: [
            {
                creditCode: "36",
                creditName: "Procurement and Workforce Inclusion",
                levelPoints: { credit: 2, exceptional: 1, minimumExpectation: 0 },
                levelSensitiveTargetIds: ["nat-5-spend-verified-supplier"],
                levelValues: { credit: 2, exceptional: 4, minimumExpectation: null },
                passesWhen: "A social procurement strategy, and 1% of contract value directed to target groups for 1 point, 2% for 2 (4% for Exceptional)",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSB_GUIDELINES}, p.266`
                },
                steps: {
                    // GBCA's calculator scores the steps once the Social Procurement
                    // Strategy is confirmed (REZ_Strategy); certificates and contracts
                    // are evidence, not a gate on the points.
                    gateTargetIds: ["nat-5-document-proc-plan"],
                    levelId: "credit",
                    targetId: "nat-5-spend-verified-supplier",
                    thresholds: [
                        { atLeast: 1, points: 1 },
                        { atLeast: 2, points: 2 }
                    ]
                },
                targetIds: [
                    "nat-5-spend-verified-supplier",
                    "nat-5-hours-by-target-group",
                    "nat-5-document-proc-plan",
                    "nat-5-document-contracts",
                    "nat-5-document-supplier-certs"
                ],
                thresholdProvenance: {
                    grade: "verified",
                    note: "The numerator also counts workforce targets converted to cost; Catchment counts certified supplier spend only. Of the four eligible supplier groups (p.267), migrant groups have no entity type, so their spend is left out too.",
                    source: `${GSB_GUIDELINES}, pp.267-268`
                }
            },
            {
                coverageSources: {
                    credit: {
                        kind: "adoptedFramework",
                        minApplicable: 10,
                        requirementName: "RAP Deliverables Met"
                    }
                },
                creditCode: "35",
                creditName: "First Nations Inclusion",
                levelPoints: { credit: 2, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                levelValues: {
                    credit: 90,
                    exceptional: null,
                    minimumExpectation: null
                },
                // The design route ("one of", p.262 / p.201) is deferred in #119.
                passesWhen: "Cultural competency training, and 90% of at least 10 applicable RAP deliverables met and publicly reported. Catchment tracks the RAP route only; the First Nations design route is not modelled",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSB_GUIDELINES}, p.261`
                },
                targetIds: [
                    "nat-5-document-cultural-training",
                    "nat-5-document-rap",
                    "nat-5-document-rap-public-report"
                ],
                thresholdProvenance: {
                    grade: "verified",
                    note: "RAP attainment is counted from the deliverables of the Framework Library framework the project links to this credit, never declared (issue #32).",
                    source: `${GSB_GUIDELINES}, p.262`
                }
            },
            {
                // Training at ME, health-program coverage at Credit (p.257).
                coverageSources: {
                    credit: {
                        kind: "employeeRegister",
                        requirementName: "Workers Covered by a Health Program"
                    },
                    minimumExpectation: {
                        kind: "employeeRegister",
                        requirementName: "Workers Trained"
                    }
                },
                creditCode: "34",
                creditName: "Inclusive Construction Practices",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                levelValues: { credit: 80, exceptional: null, minimumExpectation: 95 },
                passesWhen: "Inclusive facilities and PPE, conduct policies and training for 95% of workers on site 3+ days; health programs covering 80%, evaluated",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSB_GUIDELINES}, p.256`
                },
                targetIds: [
                    "nat-5-document-facilities",
                    "nat-5-document-site-conduct-policy",
                    "nat-5-document-site-leadership",
                    "nat-5-document-training-register",
                    "nat-5-document-needs-analysis",
                    "nat-5-document-health-programs",
                    "nat-5-document-program-evaluation"
                ],
                // Recommended evidence (pp.259-260); the needs analysis, programs and
                // evaluation are Credit Achievement only.
                targetMinLevel: {
                    "nat-5-document-health-programs": "credit",
                    "nat-5-document-needs-analysis": "credit",
                    "nat-5-document-program-evaluation": "credit"
                },
                thresholdProvenance: {
                    grade: "verified",
                    note: "ME = training coverage, Credit = health-program coverage (3 areas under $10m, 5 over). Each is counted from the employee register the project links at its level, never declared (issues #33, #119).",
                    source: `${GSB_GUIDELINES}, pp.257-258`
                }
            },
            {
                // The waste target measures diversion; training is counted beside it.
                coverageSources: {
                    minimumExpectation: {
                        kind: "employeeRegister",
                        requirementName: "Workers Given Sustainability Training",
                        threshold: 95,
                        thresholdProvenance: {
                            grade: "verified",
                            source: `${GSB_GUIDELINES}, p.46`
                        }
                    }
                },
                creditCode: "2",
                creditName: "Responsible Construction",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: ["nat-5-environmental-waste-diverted"],
                levelValues: { credit: 90, exceptional: null, minimumExpectation: 80 },
                passesWhen: "An EMS (certified over $10m), an EMP, sustainability training for 95% of workers, and 80% of C&D waste diverted; 90% with verified reporting for the point",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSB_GUIDELINES}, p.45`
                },
                targetIds: [
                    "nat-5-environmental-waste-diverted",
                    "nat-5-document-ems",
                    "nat-5-document-emp",
                    "nat-5-document-emp-audits",
                    "nat-5-document-cumulative-waste",
                    "nat-5-document-waste-disclosure",
                    "nat-5-document-sustainability-training",
                    "nat-5-document-waste-verification"
                ],
                // Recommended evidence (pp.49-50); the verification summaries prove
                // the 90% diversion, which is Credit Achievement only.
                targetMinLevel: { "nat-5-document-waste-verification": "credit" },
                thresholdProvenance: {
                    grade: "verified",
                    note: "Diversion by mass, as the share of reported C&D tonnes sent to Recycled or Reuse. GBCA excludes special waste — asbestos, hazardous and restricted solid waste (rule p.48, definition p.47) — so only rows marked non-hazardous count; restricted solid waste not marked hazardous still does. Excavation waste files under Spoil / Fill / Soil, outside C&D.",
                    source: `${GSB_GUIDELINES}, pp.45-47`
                }
            },
            {
                creditCode: "5",
                creditName: "Responsible Procurement",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "An ISO 20400 risk and opportunity assessment of 10+ key supply-chain items, including 2+ building services and 1+ building material, and a responsible procurement plan in tender documents",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSB_GUIDELINES}, p.68`
                },
                // Recommended evidence (p.71): the plan in tender documents, and its
                // impact and data reporting through construction.
                targetIds: [
                    "nat-5-document-iso20400",
                    "nat-5-document-proc-tender-extracts",
                    "nat-5-document-proc-impact-reporting"
                ]
            }
        ],
        defaultLevelId: "credit",
        defaultRatingId: "5-star",
        frameworkId: FrameworkId.NAT_5,
        levels: greenStarLevels,
        ratingScale: [
            {
                id: "4-star",
                label: "4 Star",
                requires: GSB_STAR_REQUIRES,
                threshold: 15
            },
            {
                id: "5-star",
                label: "5 Star",
                requires: GSB_HIGHER_STAR_REQUIRES,
                threshold: 35
            },
            {
                id: "6-star",
                label: "6 Star",
                requires: GSB_HIGHER_STAR_REQUIRES,
                threshold: 70
            }
        ],
        ratingScaleProvenance: {
            grade: "verified",
            note: "Out of 100 points, never rebased: no credit can be claimed Not Applicable (p.26). Every star also needs the Minimum Expectations, with more at 5 and 6 Star (p.28), and the Climate Positive Pathway, whose criteria tighten with registration year and star (pp.21-22), which the project record does not hold. The Nature Positive Pathway applies to registrations from 2028 (p.22) and is not shown until then.",
            source: `${GSB_GUIDELINES}, pp.21-22, 26, 28, 32`
        },
        reviewedOn: REVIEWED_ON,
        scoringModel: "absolutePoints"
    },
    {
        credits: [
            {
                creditCode: "2",
                creditName: "Responsible Construction Practices",
                levelPoints: { credit: 2, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: ["nat-6-environmental-waste-diverted"],
                levelValues: { credit: 90, exceptional: null, minimumExpectation: 80 },
                passesWhen: "An EMS (certified over $10m), an EMP, sustainability training for head contractors' teams, waste contractor disclosure statements, and 80% of site waste diverted; 90% with verified reporting for the points",
                pointsProvenance: {
                    grade: "verified",
                    note: "The Minimum Expectation criteria table (p.16) numbers this credit 1; the credit list (p.14) and the credit itself (p.34) number it 2.",
                    source: `${GSC_GUIDELINES}, p.14`
                },
                targetIds: [
                    "nat-6-environmental-waste-diverted",
                    "nat-6-document-ems",
                    "nat-6-document-emp",
                    "nat-6-document-waste-disclosure",
                    "nat-6-document-sustainability-training",
                    "nat-6-document-waste-verification"
                ],
                // Recommended evidence (p.37). Disclosure at ME; the verification
                // summaries prove reporting compliance, Credit Achievement only (p.36).
                targetMinLevel: { "nat-6-document-waste-verification": "credit" },
                thresholdProvenance: {
                    grade: "verified",
                    note: "Diversion by mass, as the share of reported C&D tonnes sent to Recycled or Reuse. GBCA excludes special waste — asbestos, hazardous and restricted solid waste (rule p.37, definition p.39) — so only rows marked non-hazardous count; restricted solid waste not marked hazardous still does. Excavation waste files under Spoil / Fill / Soil, outside C&D.",
                    source: `${GSC_GUIDELINES}, p.36`
                }
            },
            {
                creditCode: "3",
                creditName: "Responsible Procurement",
                levelPoints: { credit: 1, exceptional: 1, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "An ISO 20400 assessment and plan for 10 key items with hazardous materials managed; the plan extended to 20 items for the point, plus circularity measured for Exceptional",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSC_GUIDELINES}, pp.40-42`
                },
                targetIds: [
                    "nat-6-document-iso20400",
                    "nat-6-document-hazardous-materials",
                    "nat-6-document-procurement-plan-extended",
                    "nat-6-document-circularity"
                ],
                // Credit Achievement is the extended plan OR circularity; Exceptional
                // is both. The plan is the route taken at Credit Achievement.
                targetMinLevel: {
                    "nat-6-document-circularity": "exceptional",
                    "nat-6-document-procurement-plan-extended": "credit"
                }
            },
            {
                creditCode: "33",
                creditName: "First Nations Inclusion",
                levelPoints: { credit: 2, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "Cultural competency training and a First Nations site analysis; a First Nations engagement and inclusion plan (or First Nations leadership) for the points",
                pointsProvenance: {
                    grade: "verified",
                    note: "The credit pages are headed Credit 34; the credit list (p.15) numbers it 33, and Design for Equity is 34.",
                    source: `${GSC_GUIDELINES}, pp.15, 186-188`
                },
                targetIds: [
                    "nat-6-document-cultural-training",
                    "nat-6-document-site-analysis",
                    "nat-6-document-engagement-plan"
                ],
                targetMinLevel: { "nat-6-document-engagement-plan": "credit" }
            },
            {
                creditCode: "35",
                creditName: "Inclusive Site Practices",
                levelPoints: { credit: 0, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "Inclusive facilities and PPE, diversity and conduct policies with training, a health needs analysis, and health programs covering at least five areas, evaluated yearly. Minimum Expectation only, no points",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSC_GUIDELINES}, pp.196-198`
                },
                targetIds: [
                    "nat-6-document-facilities",
                    "nat-6-document-site-conduct-policy",
                    "nat-6-document-needs-analysis",
                    "nat-6-document-health-programs",
                    "nat-6-document-program-evaluation"
                ]
            },
            {
                creditCode: "36",
                creditName: "Procurement and Workforce Inclusion",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "A social procurement plan, and at least three initiatives delivered: an employment strategy, a procurement partnership and economic support",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSC_GUIDELINES}, pp.200-202`
                },
                targetIds: [
                    "nat-6-spend-verified-supplier",
                    "nat-6-hours-by-target-group",
                    "nat-6-document-proc-plan",
                    "nat-6-document-contracts",
                    "nat-6-document-supplier-certs",
                    "nat-6-document-initiatives"
                ]
            },
            {
                creditCode: "37",
                creditName: "Economic Inclusion",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "A public economic inclusion program with at least five strategies or programs for under-represented groups",
                pointsProvenance: {
                    grade: "verified",
                    note: "Scoped to commerce and business within the precinct (p.206), so construction-phase social-enterprise spend is not evidence here; it belongs to credit 36.",
                    source: `${GSC_GUIDELINES}, pp.206-207`
                },
                targetIds: ["nat-6-document-economic-inclusion-plan"]
            }
        ],
        defaultLevelId: "credit",
        defaultRatingId: "5-star",
        frameworkId: FrameworkId.NAT_6,
        levels: greenStarLevels,
        ratingScale: [
            {
                id: "4-star",
                label: "4 Star",
                requires: GSC_STAR_REQUIRES,
                threshold: 10
            },
            {
                id: "5-star",
                label: "5 Star",
                requires: GSC_STAR_REQUIRES,
                threshold: 35
            },
            {
                id: "6-star",
                label: "6 Star",
                requires: GSC_STAR_REQUIRES,
                threshold: 70
            }
        ],
        ratingScaleProvenance: {
            grade: "verified",
            note: "Out of 100 points; every star also needs all Minimum Expectations and the Climate and Nature Positive Pathways (p.12). Both pathways phase in by registration year (pp.19-20); Nature Positive binds from 2027 registrations and is not shown until then.",
            source: `${GSC_GUIDELINES}, pp.12, 19-20`
        },
        reviewedOn: "2026-09-23",
        scoringModel: "absolutePoints"
    },
    {
        credits: [
            {
                creditCode: "8",
                creditName: "Responsible Material Management",
                levelPoints: { credit: 1, exceptional: 1, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "Material outflows reported; comprehensive forecast-vs-actual reporting for the point, plus on-site separation of material streams for Exceptional",
                pointsProvenance: {
                    grade: "verified",
                    note: "Credit Achievement is one of off-site reuse (10%), on-site separation or comprehensive reporting; Exceptional is two. The summary (p.63) asks for one separated stream, the requirement (p.64) for two.",
                    source: `${GSF_GUIDELINES}, pp.63-65`
                },
                targetIds: [
                    "nat-7-environmental-waste-diverted",
                    "nat-7-document-material-outflows",
                    "nat-7-document-comprehensive-reporting",
                    "nat-7-document-onsite-separation"
                ],
                targetMinLevel: {
                    "nat-7-document-comprehensive-reporting": "credit",
                    "nat-7-document-onsite-separation": "exceptional"
                },
                thresholdProvenance: {
                    grade: "verified",
                    note: "Material outflows are a VOLUME: total m³ removed from site (p.64), 660 L bins converted at 0.66 m³ each (p.66), and the off-site reuse route divides m³ by m³ (p.67). Mass is never the credit's metric. Catchment carries C&D waste in tonnes, so the waste row is report-only here and no share is computed from it. Issue #104.",
                    source: `${GSF_GUIDELINES}, pp.64, 66-67`
                }
            },
            {
                creditCode: "10",
                creditName: "Responsible Procurement",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "An ISO 20400 plan covering 5 or more key items, embedded in tender documentation",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSF_GUIDELINES}, pp.14, 73-75`
                },
                targetIds: ["nat-7-document-iso20400"]
            },
            {
                coverageSources: {
                    credit: {
                        kind: "employeeRegister",
                        requirementName: "Workers Covered by a Health Program"
                    }
                },
                creditCode: "29",
                creditName: "Construction Site Culture",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                levelValues: {
                    credit: 80,
                    exceptional: null,
                    minimumExpectation: null
                },
                passesWhen: "Inclusive facilities and PPE and on-site policies; programs across three or more areas of physical or mental health, in place from 1 month after site works start to practical completion, covering 80% of workers on site more than five consecutive days, and evaluated, for the point",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSF_GUIDELINES}, pp.15, 174`
                },
                targetIds: [
                    "nat-7-document-facilities",
                    "nat-7-document-site-conduct-policy",
                    "nat-7-document-health-programs",
                    "nat-7-document-program-evaluation"
                ],
                targetMinLevel: {
                    "nat-7-document-health-programs": "credit",
                    "nat-7-document-program-evaluation": "credit"
                },
                thresholdProvenance: {
                    grade: "verified",
                    note: "Health-program coverage, counted from the employee register the project links to this credit, never declared (issue #33).",
                    source: `${GSF_GUIDELINES}, p.176`
                }
            },
            {
                coverageSources: {
                    credit: {
                        kind: "adoptedFramework",
                        // "at least three" is the scheme's own floor here, not v1.1's ten.
                        minApplicable: 3,
                        requirementName: "RAP Deliverables Met"
                    }
                },
                creditCode: "30",
                creditName: "First Nations Equity (AU only)",
                levelPoints: { credit: 2, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                levelValues: {
                    credit: 70,
                    exceptional: null,
                    minimumExpectation: null
                },
                passesWhen: "Cultural competency training, and either 70% (at least three) of the applicable deliverables of a delivery organisation's RAP, or First Nations design: engagement during design, a built design outcome it informed, and a visible cultural element",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSF_GUIDELINES}, pp.15, 180`
                },
                targetIds: [
                    "nat-7-document-cultural-training",
                    "nat-7-document-rap-deliverables"
                ],
                thresholdProvenance: {
                    grade: "verified",
                    note: "Credit Achievement is Cultural Capability AND ONE OF the RAP route or Inclusion of First Nations Design (pp.180-182), both at Credit Achievement, so neither can be gated by level. The one requirement carries both routes, as credit 22.0 does on Design & As Built. The 70% is the RAP route's threshold only; the design route sets no percentage. RAP attainment is counted from the deliverables of the Framework Library framework the project links to this credit, never declared (issue #32).",
                    source: `${GSF_GUIDELINES}, pp.181-182`
                }
            },
            {
                creditCode: "32",
                creditName: "Procurement and Workforce Inclusion",
                levelPoints: { credit: 1, exceptional: 1, minimumExpectation: 0 },
                levelSensitiveTargetIds: ["nat-7-spend-verified-supplier"],
                levelValues: { credit: 1, exceptional: 1, minimumExpectation: null },
                passesWhen: "1% of contract value to certified social value partners for the point, plus 1% to economic support initiatives for Exceptional",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSF_GUIDELINES}, pp.15, 191-192`
                },
                targetIds: [
                    "nat-7-spend-verified-supplier",
                    "nat-7-document-economic-support"
                ],
                // Credit Achievement is partners OR economic support; Exceptional is
                // both. Partner spend is the route Catchment measures.
                targetMinLevel: { "nat-7-document-economic-support": "exceptional" },
                thresholdProvenance: {
                    grade: "verified",
                    source: `${GSF_GUIDELINES}, p.192`
                }
            }
        ],
        defaultLevelId: "credit",
        defaultRatingId: "5-star",
        frameworkId: FrameworkId.NAT_7,
        levels: greenStarLevels,
        ratingScale: [
            {
                id: "4-star",
                label: "4 Star",
                requires: [ALL_MINIMUM_EXPECTATIONS],
                threshold: 0
            },
            {
                id: "5-star",
                label: "5 Star",
                requires: [ALL_MINIMUM_EXPECTATIONS, CLIMATE_POSITIVE],
                threshold: 20
            },
            {
                id: "6-star",
                label: "6 Star",
                requires: [ALL_MINIMUM_EXPECTATIONS, CLIMATE_POSITIVE],
                threshold: 45
            }
        ],
        ratingScaleProvenance: {
            grade: "verified",
            note: "Out of 60 points; 4 Star is all Minimum Expectations, 5 and 6 Star also need the Climate Positive Pathway.",
            source: `${GSF_GUIDELINES}, p.11`
        },
        reviewedOn: "2026-09-22",
        scoringModel: "mePlusPoints"
    },
    {
        // No Exceptional Performance except the P&WI substitution. Credit 21 is
        // parked: only GBCA's calculator scores it. v1.2 only, decisions.md §22.
        credits: [
            {
                creditCode: "7.0",
                creditGroup: { code: "7", name: "Responsible Construction Practices" },
                creditName: "Environmental Management Plan",
                levelPoints: { credit: 0, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "A site-specific construction environmental management plan in place from the start of works. Required before 7.1 or 7.2 count",
                pointsProvenance: {
                    grade: "verified",
                    source: `${DAB_GUIDELINES}, p.77`
                },
                targetIds: ["nat-10-document-emp"]
            },
            {
                creditCode: "7.1",
                creditGroup: { code: "7", name: "Responsible Construction Practices" },
                creditName: "Environmental Management System",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "An audited environmental management system, independently certified (e.g. ISO 14001) when the contract is $10m or more",
                pointsProvenance: {
                    grade: "verified",
                    source: `${DAB_GUIDELINES}, p.78`
                },
                targetIds: ["nat-10-document-ems"]
            },
            {
                creditCode: "7.2",
                creditGroup: { code: "7", name: "Responsible Construction Practices" },
                creditName: "High Quality Staff Support",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "Wellbeing programmes covering 3+ issues including mental health, and sustainability training for every worker on site 3+ days",
                pointsProvenance: {
                    grade: "verified",
                    source: `${DAB_GUIDELINES}, pp.78-79`
                },
                targetIds: [
                    "nat-10-document-wellbeing-register",
                    "nat-10-document-sustainability-training"
                ]
            },
            {
                creditCode: "22B",
                creditGroup: { code: "22", name: "Construction and Demolition Waste" },
                creditName: "Percentage Benchmark",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: ["nat-10-environmental-waste-diverted"],
                levelValues: {
                    credit: 90,
                    exceptional: null,
                    minimumExpectation: null
                },
                passesWhen: "90% of C&D waste diverted from landfill by mass, in a cumulative waste report, from contractors with a Compliance Verification Summary or Disclosure Statement",
                pointsProvenance: {
                    grade: "verified",
                    source: `${DAB_GUIDELINES}, pp.255-256`
                },
                targetIds: [
                    "nat-10-environmental-waste-diverted",
                    "nat-10-document-waste-reporting",
                    "nat-10-document-cumulative-waste"
                ],
                thresholdProvenance: {
                    grade: "verified",
                    note: "Diversion by mass, as the share of reported C&D tonnes sent to Recycled or Reuse. GBCA excludes special waste — asbestos, hazardous and restricted solid waste (rule and definition p.257) — so only rows marked non-hazardous count; restricted solid waste not marked hazardous still does. Excavation waste files under Spoil / Fill / Soil, outside C&D. The contractor verification is the 22.0 minimum requirement.",
                    source: `${DAB_GUIDELINES}, pp.256-257, 259`
                }
            },
            {
                creditCode: "22A",
                creditGroup: { code: "22", name: "Construction and Demolition Waste" },
                creditName: "Fixed Benchmark",
                isParked: true,
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "Under 10 kg of waste to landfill per m² of floor area. Needs a floor-area field",
                pointsProvenance: {
                    grade: "verified",
                    note: "Half a point at 12.5 to 15 kg/m² (Table 22A.1), which the whole-point model cannot hold yet.",
                    source: `${DAB_GUIDELINES}, p.256`
                },
                targetIds: []
            },
            {
                creditCode: "20.1",
                creditGroup: { code: "20", name: "Responsible Building Materials" },
                creditName: "Structural and Reinforcing Steel",
                // Shown as upcoming: GBCA scores steel by mass, and an eco-label
                // share is weighted by dollars only so far (issue #87).
                isParked: true,
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                levelValues: {
                    credit: 95,
                    exceptional: null,
                    minimumExpectation: null
                },
                passesWhen: "95% of steel by mass from a responsible steel maker",
                pointsProvenance: {
                    grade: "verified",
                    source: `${DAB_GUIDELINES}, pp.233-234`
                },
                targetIds: [
                    "nat-10-environmental-steel",
                    "nat-10-document-steel-certificates"
                ],
                thresholdProvenance: {
                    grade: "verified",
                    note: "A Responsible Steel Maker is a supplier certification, not a product eco-label: the mill holds ISO 14001 and the maker is a worldsteel Climate Action member. Plus 60% from an ASI Environmental Sustainability Charter fabricator (steel frame) or 60% of reo by mass from energy-reducing processes (concrete frame). N/A when steel is under 1% of contract value.",
                    source: `${DAB_GUIDELINES}, pp.233-234`
                }
            },
            {
                creditCode: "20.2",
                creditGroup: { code: "20", name: "Responsible Building Materials" },
                creditName: "Timber",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: ["nat-10-environmental-timber"],
                levelValues: {
                    credit: 95,
                    exceptional: null,
                    minimumExpectation: null
                },
                passesWhen: "95% of timber by cost certified (FSC or PEFC) or reused, formwork included",
                pointsProvenance: {
                    grade: "verified",
                    source: `${DAB_GUIDELINES}, pp.233, 235`
                },
                targetIds: [
                    "nat-10-environmental-timber",
                    "nat-10-document-timber-chain-of-custody"
                ],
                thresholdProvenance: {
                    grade: "verified",
                    note: "Measured as the share of reported timber dollars labelled FSC (ISEAL) or PEFC. Reused timber counts for GBCA but has no field on a materials row, so it is not counted. Bamboo and cork are out of scope. N/A under 0.1% of contract value is not modelled.",
                    source: `${DAB_GUIDELINES}, pp.233, 235, 239`
                }
            },
            {
                creditCode: "20.3",
                creditGroup: { code: "20", name: "Responsible Building Materials" },
                creditName: "Permanent Formwork, Pipes, Flooring, Blinds and Cables",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                levelValues: {
                    credit: 90,
                    exceptional: null,
                    minimumExpectation: null
                },
                passesWhen: "90% by cost of permanent formwork, pipes, flooring, blinds and cables PVC-free (with an SDS or EPD) or meeting GBCA's PVC Best Practice Guidelines",
                pointsProvenance: {
                    grade: "verified",
                    source: `${DAB_GUIDELINES}, p.233`
                },
                targetIds: ["nat-10-document-pvc-declarations"],
                thresholdProvenance: {
                    grade: "verified",
                    note: "Evidence only, not a measured share: the denominator is the cost of the PVC products among those five uses, and a materials row can neither be flagged as containing PVC nor filed as formwork, blinds or vinyl flooring. N/A under 1% of contract value.",
                    source: `${DAB_GUIDELINES}, pp.233, 236, 240`
                }
            },
            {
                creditCode: "21",
                creditName: "Sustainable Products",
                isParked: true,
                levelPoints: { credit: 3, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "A sustainability-weighted share of product cost, scored by GBCA's calculator",
                pointsProvenance: {
                    grade: "verified",
                    note: "Up to 3 points, stepped 1/2/3 at 3/6/9% in GBCA's example table.",
                    source: `${DAB_GUIDELINES}, pp.245-247`
                },
                targetIds: []
            },
            {
                creditCode: "13.1",
                creditGroup: { code: "13", name: "Indoor Pollutants" },
                creditName: "Paints, Adhesives, Sealants and Carpets",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                levelValues: {
                    credit: 95,
                    exceptional: null,
                    minimumExpectation: null
                },
                passesWhen: "95% of internally applied paints, adhesives and sealants (by volume) or carpets (by area) meet the Total VOC limits, by product certification or lab testing, or none are used",
                pointsProvenance: {
                    grade: "verified",
                    source: `${DAB_GUIDELINES}, p.127`
                },
                targetIds: ["nat-10-document-low-voc"],
                thresholdProvenance: {
                    grade: "verified",
                    note: "Evidence only: a share by volume and area, not by cost, so the dollar-weighted eco-label share cannot measure it. Limits in Table 13.1.1 (g/L) and the carpet table.",
                    source: `${DAB_GUIDELINES}, pp.127-129`
                }
            },
            {
                creditCode: "13.2",
                creditGroup: { code: "13", name: "Indoor Pollutants" },
                creditName: "Engineered Wood Products",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                levelValues: {
                    credit: 95,
                    exceptional: null,
                    minimumExpectation: null
                },
                passesWhen: "95% of engineered wood (by area) meets the formaldehyde limits, by product certification or lab testing, or no new engineered wood is used. Formwork excluded",
                pointsProvenance: {
                    grade: "verified",
                    source: `${DAB_GUIDELINES}, p.127`
                },
                targetIds: ["nat-10-document-low-formaldehyde"],
                thresholdProvenance: {
                    grade: "verified",
                    note: "Evidence only: a share by area, not by cost. Limits in Table 13.2B.",
                    source: `${DAB_GUIDELINES}, pp.130-131`
                }
            },
            {
                creditCode: "30E-PWI",
                creditGroup: { code: "30", name: "Innovation" },
                creditName: "Procurement and Workforce Inclusion",
                isInnovation: true,
                levelPoints: { credit: 1, exceptional: 1, minimumExpectation: 0 },
                levelSensitiveTargetIds: ["nat-10-spend-verified-supplier"],
                levelValues: { credit: 2, exceptional: 4, minimumExpectation: null },
                passesWhen: "Green Star Buildings credit 36 claimed as Innovation: 2% of contract value directed to target groups (4% for Exceptional)",
                pointsProvenance: {
                    grade: "verified",
                    source: `${SUBSTITUTION_GUIDE}, p.10`
                },
                targetIds: [
                    "nat-10-spend-verified-supplier",
                    "nat-10-hours-by-target-group",
                    "nat-10-document-proc-plan",
                    "nat-10-document-contracts",
                    "nat-10-document-supplier-certs"
                ],
                thresholdProvenance: {
                    grade: "corroborated",
                    note: "Which Buildings version applies to a substitution is not stated.",
                    source: NSW_SCORECARD
                }
            },
            {
                coverageSources: {
                    credit: {
                        kind: "employeeRegister",
                        requirementName: "Workers Covered by a Health Program"
                    }
                },
                creditCode: "30E-ICP",
                creditGroup: { code: "30", name: "Innovation" },
                creditName: "Inclusive Construction Practices",
                isInnovation: true,
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                // No Minimum Expectation: Design & As Built gates through credit-level
                // minimum and Conditional Requirements instead (p.8), and awards an
                // Innovation point whole (p.311). No 95% training rung from Buildings.
                levelValues: {
                    credit: 80,
                    exceptional: null,
                    minimumExpectation: null
                },
                passesWhen: "Green Star Buildings credit 34 claimed as Innovation. Initiatives already counted under 7.2 cannot count again",
                pointsProvenance: {
                    grade: "verified",
                    source: `${SUBSTITUTION_GUIDE}, p.10`
                },
                targetIds: [
                    "nat-10-document-facilities",
                    "nat-10-document-training-register"
                ],
                thresholdProvenance: {
                    grade: "verified",
                    note: "Health-program coverage, from the source credit's own guidelines rather than the consultation paper that preceded them.",
                    source: `${GSB_GUIDELINES}, pp.257-258`
                }
            },
            {
                creditCode: "30E-RP",
                creditGroup: { code: "30", name: "Innovation" },
                creditName: "Responsible Procurement",
                isInnovation: true,
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "Green Star Buildings credit 5 claimed as Innovation: an ISO 20400 assessment and plan",
                pointsProvenance: {
                    grade: "verified",
                    source: `${SUBSTITUTION_GUIDE}, p.9`
                },
                targetIds: ["nat-10-document-iso20400"]
            },
            {
                creditCode: "30D-RAP",
                creditGroup: { code: "30", name: "Innovation" },
                creditName: "Reconciliation Action Plan",
                isInnovation: true,
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "The Reconciliation Action Plan Innovation Challenge claimed: a delivery organisation's endorsed RAP, with the deliverables it implemented on the project reported publicly",
                pointsProvenance: {
                    grade: "verified",
                    note: "Reconciliation Action Plan is a current Innovation Challenge for this tool (p.316), worth one point like every initiative (p.311), inside the 10-point Innovation cap (p.319). GBCA publishes the Challenge brief on its website rather than in the guidelines, so the evidence follows what Buildings credit 35 asks for. No threshold is documented, so none is set.",
                    source: `${DAB_GUIDELINES}, pp.311, 316, 319`
                },
                targetIds: ["nat-10-document-rap", "nat-10-document-rap-public-report"]
            }
        ],
        defaultLevelId: "credit",
        defaultRatingId: "5-star",
        frameworkId: FrameworkId.NAT_10,
        levels: greenStarLevels,
        ratingScale: [
            {
                id: "4-star",
                label: "4 Star",
                requires: [DAB_CONDITIONAL_REQUIREMENTS],
                threshold: 45
            },
            {
                id: "5-star",
                label: "5 Star",
                requires: [
                    DAB_CONDITIONAL_REQUIREMENTS,
                    "At least 3 points from credit 15 Greenhouse Gas Emissions, through energy efficiency or on-site renewables"
                ],
                threshold: 60
            },
            {
                id: "6-star",
                label: "6 Star",
                requires: [
                    DAB_CONDITIONAL_REQUIREMENTS,
                    "At least 6 points from credit 15 Greenhouse Gas Emissions, through energy efficiency or on-site renewables"
                ],
                threshold: 75
            }
        ],
        ratingScaleProvenance: {
            grade: "verified",
            note: "Percent of available points; Not Applicable credits leave the total. Innovation sits outside it (pp.12-13). Credit 15 sets minimum energy points for 5 and 6 Star (3 and 6, p.148), and credits 15 and 24 carry Conditional Requirements (p.8). Both are shown on each star as reference, not checked.",
            source: `${DAB_GUIDELINES}, pp.8, 12-13, 148`
        },
        reviewedOn: "2026-09-22",
        scoringModel: "percentOfAvailable"
    },
    {
        // Buildings v1: People credits are 31 to 33 (v1.1: 34 to 36), no steps.
        credits: [
            {
                creditCode: "33",
                creditName: "Procurement and Workforce Inclusion",
                levelPoints: { credit: 2, exceptional: 1, minimumExpectation: 0 },
                levelSensitiveTargetIds: ["nat-11-spend-verified-supplier"],
                levelValues: { credit: 2, exceptional: 4, minimumExpectation: null },
                passesWhen: "A social procurement strategy with targets and annual reporting, directing 2% of contract value to target groups (4% for Exceptional)",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSB1_GUIDELINES}, p.204`
                },
                targetIds: [
                    "nat-11-spend-verified-supplier",
                    "nat-11-hours-by-target-group",
                    "nat-11-document-proc-plan",
                    "nat-11-document-contracts-workforce",
                    "nat-11-document-contracts",
                    "nat-11-document-supplier-certs"
                ],
                thresholdProvenance: {
                    grade: "verified",
                    note: "The 2% also counts workforce targets converted to dollars; Catchment counts certified supplier spend only.",
                    source: `${GSB1_GUIDELINES}, pp.205-206`
                }
            },
            {
                // As v1.1 credit 34: training at ME, health programs at Credit (p.196).
                coverageSources: {
                    credit: {
                        kind: "employeeRegister",
                        requirementName: "Workers Covered by a Health Program"
                    },
                    minimumExpectation: {
                        kind: "employeeRegister",
                        requirementName: "Workers Trained"
                    }
                },
                creditCode: "31",
                creditName: "Inclusive Construction Practices",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                levelValues: { credit: 80, exceptional: null, minimumExpectation: 95 },
                passesWhen: "Inclusive facilities and PPE, conduct policies run by a diverse lead team, training for 95% of workers on site 3+ days; a needs analysis and health programs covering 80% of them, evaluated, for the point",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSB1_GUIDELINES}, p.196`
                },
                targetIds: [
                    "nat-11-document-site-conduct-policy",
                    "nat-11-document-facilities",
                    "nat-11-document-ppe",
                    "nat-11-document-lead-team-diversity",
                    "nat-11-document-training-register",
                    "nat-11-document-needs-analysis",
                    "nat-11-document-health-programs",
                    "nat-11-document-program-evaluation"
                ],
                // Recommended evidence (p.198); the needs analysis, programs and
                // evaluation are Credit Achievement only.
                targetMinLevel: {
                    "nat-11-document-health-programs": "credit",
                    "nat-11-document-needs-analysis": "credit",
                    "nat-11-document-program-evaluation": "credit"
                },
                thresholdProvenance: {
                    grade: "verified",
                    note: "ME = training coverage, Credit = health-program coverage (5 areas). Each is counted from the employee register the project links at its level, never declared (issues #33, #119).",
                    source: `${GSB1_GUIDELINES}, pp.197-198`
                }
            },
            {
                coverageSources: {
                    credit: {
                        kind: "adoptedFramework",
                        // v1 states the 90% without v1.1's "at least 10" (p.201).
                        requirementName: "RAP Targets Met"
                    }
                },
                creditCode: "32",
                creditName: "Indigenous Inclusion",
                levelPoints: { credit: 2, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                levelValues: {
                    credit: 90,
                    exceptional: null,
                    minimumExpectation: null
                },
                // The design route ("one of", p.262 / p.201) is deferred in #119.
                passesWhen: "A project team member on the organisation's RAP working group, and 90% of the RAP targets met on the project and publicly reported. Catchment tracks the RAP route only; the Indigenous design route is not modelled",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSB1_GUIDELINES}, p.200`
                },
                // Recommended evidence (p.202), all five items of the RAP route.
                targetIds: [
                    "nat-11-document-rap",
                    "nat-11-document-rap-working-group",
                    "nat-11-document-rap-public-report",
                    "nat-11-document-rap-outcomes",
                    "nat-11-document-rap-link"
                ],
                thresholdProvenance: {
                    grade: "verified",
                    note: "RAP attainment is counted from the targets of the Framework Library framework the project links to this credit, never declared (issue #32).",
                    source: `${GSB1_GUIDELINES}, p.201`
                }
            },
            {
                creditCode: "5",
                creditName: "Responsible Procurement",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: [],
                passesWhen: "An ISO 20400 risk and opportunity assessment of 10+ key supply-chain items before the head contractor is appointed, and a responsible procurement plan in tender documents, implemented through construction",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSB1_GUIDELINES}, pp.60-61`
                },
                // Recommended evidence (p.62): the plan in tender documents, and its
                // impact and data reporting through construction.
                targetIds: [
                    "nat-11-document-iso20400",
                    "nat-11-document-proc-tender-extracts",
                    "nat-11-document-proc-impact-reporting"
                ]
            },
            {
                // As v1.1 credit 2: training counted beside the waste target.
                coverageSources: {
                    minimumExpectation: {
                        kind: "employeeRegister",
                        requirementName: "Workers Given Sustainability Training",
                        threshold: 95,
                        thresholdProvenance: {
                            grade: "verified",
                            source: `${GSB1_GUIDELINES}, p.42`
                        }
                    }
                },
                creditCode: "2",
                creditName: "Responsible Construction",
                levelPoints: { credit: 1, exceptional: 0, minimumExpectation: 0 },
                levelSensitiveTargetIds: ["nat-11-environmental-waste-diverted"],
                levelValues: { credit: 90, exceptional: null, minimumExpectation: 80 },
                passesWhen: "An EMS (ISO 14001 or similar over $10m), an EMP, sustainability training for 95% of workers, and 80% of C&D waste diverted with a contractor disclosure statement; 90% with verification summaries for the point",
                pointsProvenance: {
                    grade: "verified",
                    source: `${GSB1_GUIDELINES}, p.41`
                },
                targetIds: [
                    "nat-11-environmental-waste-diverted",
                    "nat-11-document-ems",
                    "nat-11-document-emp",
                    "nat-11-document-cumulative-waste",
                    "nat-11-document-waste-disclosure",
                    "nat-11-document-sustainability-training",
                    "nat-11-document-waste-verification"
                ],
                // Recommended evidence (p.43); the verification summaries prove the
                // 90% diversion, which is Credit Achievement only.
                targetMinLevel: { "nat-11-document-waste-verification": "credit" },
                thresholdProvenance: {
                    grade: "verified",
                    note: "Diversion by mass, as the share of reported C&D tonnes sent to Recycled or Reuse. GBCA excludes special waste — asbestos, hazardous and restricted solid waste (rule p.42, definition p.45) — so only rows marked non-hazardous count; restricted solid waste not marked hazardous still does. Excavation waste files under Spoil / Fill / Soil, outside C&D.",
                    source: `${GSB1_GUIDELINES}, pp.42-43`
                }
            }
        ],
        defaultLevelId: "credit",
        defaultRatingId: "5-star",
        frameworkId: FrameworkId.NAT_11,
        levels: greenStarLevels,
        ratingScale: [
            {
                id: "4-star",
                label: "4 Star",
                requires: [
                    ALL_MINIMUM_EXPECTATIONS,
                    `${CLIMATE_POSITIVE} if registered from 2026`
                ],
                threshold: 15
            },
            {
                id: "5-star",
                label: "5 Star",
                requires: [
                    GSB_HIGHER_MINIMUM_EXPECTATIONS,
                    `${CLIMATE_POSITIVE} if registered from 2023`
                ],
                threshold: 35
            },
            {
                id: "6-star",
                label: "6 Star",
                requires: [GSB_HIGHER_MINIMUM_EXPECTATIONS, CLIMATE_POSITIVE],
                threshold: 70
            }
        ],
        ratingScaleProvenance: {
            grade: "verified",
            note: "Out of 100 points; every star also needs all Minimum Expectations, with more at 5 and 6 Star (p.25). The Climate Positive Pathway is required at 6 Star, at 5 Star for registrations from 2023, and at every star for registrations from 2026 or certification after 2030 (p.19). The 2030 rule is not shown: it turns on a certification date nothing records.",
            source: `${GSB1_GUIDELINES}, pp.19, 21-22, 25`
        },
        reviewedOn: "2026-09-22",
        scoringModel: "absolutePoints"
    }
];
const creditScopesByFrameworkId = new Map(frameworkCreditScopes.map((scope) => [scope.frameworkId, scope]));
/** The credit scope for a framework, or undefined for a policy (the normal case). */
export const findCreditScope = (frameworkId) => creditScopesByFrameworkId.get(frameworkId);
export const isRatingToolFramework = (frameworkId) => creditScopesByFrameworkId.has(frameworkId);
export const findCredit = (scope, creditCode) => scope.credits.find((credit) => credit.creditCode === creditCode);
/**
 * The value a credit's level sets for a target. A documented `null` collapses
 * to undefined: read as 0 it would seed a target permanently 100% met.
 */
export const creditTargetValueForLevel = (credit, levelId, targetId) => {
    if (!credit.levelSensitiveTargetIds.includes(targetId)) {
        return undefined;
    }
    return credit.levelValues?.[levelId] ?? undefined;
};
/**
 * Credit codes in scheme order: leading number, then the rest, so 2, 7.0,
 * 13.1, 22A, 22B, 30E-ICP. A code with no leading number sorts last.
 */
export const compareCreditCodes = (a, b) => {
    const leading = (code) => {
        const match = code.match(/^\d+(\.\d+)?/);
        return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
    };
    const difference = leading(a) - leading(b);
    return difference !== 0 ? difference : a.localeCompare(b);
};
/** A scope's credits in that order, without disturbing the catalogue array. */
export const creditsInCodeOrder = (scope) => [...scope.credits].sort((a, b) => compareCreditCodes(a.creditCode, b.creditCode));
/**
 * Where this credit counts a coverage figure from at a level. Undefined keeps
 * the credit at Not Verified. Only returned where there is a figure to count:
 * the source's own `threshold`, else the level's `levelValues`.
 */
export const creditCoverageSourceForLevel = (credit, levelId) => {
    const source = credit.coverageSources?.[levelId];
    return source && (source.threshold ?? credit.levelValues?.[levelId]) != null
        ? source
        : undefined;
};
/**
 * Every coverage figure a credit pursued at a level needs, low to high: one
 * per level at or below it with a source. Buildings v1.1 credit 34 at Credit
 * Achievement counts training (95%) and health programs (80%) apart. An
 * unknown level reads as none.
 */
export const creditCoverageThresholdsAt = (scope, credit, levelId) => {
    const reached = scope.levels.findIndex((level) => level.id === levelId);
    return scope.levels.slice(0, reached + 1).flatMap((level) => {
        const source = creditCoverageSourceForLevel(credit, level.id);
        const threshold = source?.threshold ?? credit.levelValues?.[level.id];
        return source && threshold != null
            ? [{ levelId: level.id, source, threshold }]
            : [];
    });
};
/**
 * The id of the Performance requirement one level's coverage figure is, one
 * per level. Prefixed so no catalogue target id, none of which starts `coverage:`,
 * can collide.
 */
export const coverageTargetId = (creditCode, levelId) => `coverage:${creditCode}@${levelId}`;
/**
 * Levels below the pursued one whose threshold nothing counts, low to high.
 * A lower level with a coverage source is counted by its own figure, and one
 * without is carried by the credit's level-sensitive target if it has one.
 * What is left, such as Design & As Built 13.1's 95% at Exceptional, holds the
 * credit at Not Verified. decisions.md §22.
 */
export const unmeasuredLowerLevelIds = (scope, credit, levelId) => {
    const reached = scope.levels.findIndex((level) => level.id === levelId);
    return scope.levels
        .slice(0, Math.max(0, reached))
        .map((level) => level.id)
        .filter((lower) => credit.levelValues?.[lower] != null &&
        !creditCoverageSourceForLevel(credit, lower) &&
        credit.levelSensitiveTargetIds.length === 0);
};
/**
 * Every threshold a credit pursued at a level needs and Catchment cannot
 * count: lower levels {@link unmeasuredLowerLevelIds} finds, then the
 * `unmeasuredThresholds` of that level and those below it. Any one holds the
 * credit at Not Verified.
 */
export const unmeasuredThresholdsAt = (scope, credit, levelId) => {
    const labelOf = (id) => scope.levels.find((level) => level.id === id)?.label ?? id;
    const lower = unmeasuredLowerLevelIds(scope, credit, levelId).map((lowerId) => ({
        levelId: lowerId,
        name: `${labelOf(lowerId)} threshold`,
        value: credit.levelValues?.[lowerId] ?? 0
    }));
    const reached = Math.max(0, scope.levels.findIndex((level) => level.id === levelId));
    const stated = scope.levels
        .slice(0, reached + 1)
        .flatMap((level) => (credit.unmeasuredThresholds?.[level.id] ?? []).map(({ name, value }) => ({ levelId: level.id, name, value })));
    return [...lower, ...stated];
};
/**
 * The targets a credit asks for at a level. An unknown level reads as the
 * lowest, so a stale stored level never switches on more than it should.
 */
export const creditTargetIdsAtLevel = (scope, credit, levelId) => {
    const rank = (id) => Math.max(0, scope.levels.findIndex((level) => level.id === id));
    const reached = rank(levelId);
    return credit.targetIds.filter((targetId) => {
        const from = credit.targetMinLevel?.[targetId];
        return from === undefined || rank(from) <= reached;
    });
};
/** Points a credit is worth when pursued at a level: that level plus every level below it. */
export const creditPointsAtLevel = (scope, credit, levelId) => {
    const reached = scope.levels.findIndex((level) => level.id === levelId);
    return scope.levels
        .slice(0, reached + 1)
        .reduce((total, level) => total + (credit.levelPoints[level.id] ?? 0), 0);
};
/**
 * Points a credit earns up to a level, given what its measured target
 * achieved: each level whose figure is met, stepped where it has `steps`. A
 * level counts only while every level below it is met, so Exceptional
 * Performance never scores without Credit Achievement (credit 36's calculator,
 * `C39`), and a credit pursued at Exceptional still earns the levels it did
 * reach (Buildings v1 credit 33: 3% against 4% is Credit Achievement's 2).
 */
export const creditPointsForAchievement = (scope, credit, levelId, achieved) => {
    const steps = credit.steps;
    const reached = scope.levels.findIndex((level) => level.id === levelId);
    let total = 0;
    for (const level of scope.levels.slice(0, reached + 1)) {
        if (level.id === steps?.levelId) {
            const stepped = steps.thresholds
                .filter((step) => achieved >= step.atLeast)
                .reduce((best, step) => Math.max(best, step.points), 0);
            total += stepped;
            if (stepped === 0) {
                break;
            }
            continue;
        }
        const value = credit.levelValues?.[level.id];
        // Written as "not met" rather than `achieved < value`: a NaN share then
        // meets nothing, where `<` would read it as meeting every level.
        if (value != null && !(achieved >= value)) {
            break;
        }
        total += credit.levelPoints[level.id] ?? 0;
    }
    return total;
};
/** Documents evidence a credit; every measurable target is performance. */
export const creditTrackFor = (target) => target.targetCategory === FrameworkTargetCategory.Documents
    ? "evidence"
    : "performance";
// ── IS rating tools (parked) ───────────────────────────────────────────────
//
// Kept in the old shape for reference only. Every IS credit code, name and
// number below is wrong (issue #27), and IS awards fractional levels this
// model cannot hold. Re-identify before reviving.
//
// const isRatingLevels = [
//   { id: "level1", label: "Level 1", shortLabel: "L1" },
//   { id: "level2", label: "Level 2", shortLabel: "L2" },
//   { id: "level3", label: "Level 3", shortLabel: "L3" }
// ];
//
// const isParkedScopes = [
//   {
//     credits: [
//       {
//         creditCode: "Spr-2",
//         creditName: "Sustainable Procurement Implementation",
//         levelNotes: {
//           level1: "≥1% of contract value with verified suppliers",
//           level2: "≥2% of contract value (stretch, +2 pts)",
//           level3:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual"
//         },
//         levelSensitiveTargetIds: ["nat-8-spend-verified-supplier"],
//         levelValues: { level1: 1, level2: 2, level3: null },
//         passesWhen:
//           "Spend at least 1% of contract value with verified suppliers (2% at Level 2) + FTE reporting",
//         points: 3,
//         setUpFields: [
//           "Total contract value",
//           "Social-spend target %",
//           "Accepted certification bodies"
//         ],
//         targetIds: [
//           "nat-8-spend-verified-supplier",
//           "nat-8-hours-fte-by-group",
//           "nat-8-document-sustainable-procurement-strategy-iso-20400",
//           "nat-8-document-contracts-showing-targets-written-in"
//         ]
//       },
//       {
//         creditCode: "Leg-1",
//         creditName: "Indigenous Participation & Legacy",
//         levelNotes: {
//           level1: "≥80% of RAP actions met",
//           level2:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual",
//           level3:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual"
//         },
//         levelSensitiveTargetIds: [],
//         levelValues: { level1: 80, level2: null, level3: null },
//         passesWhen:
//           "Indigenous Participation Plan (RAP) with at least 80% of actions met",
//         points: 2,
//         setUpFields: ["RAP targets"],
//         targetIds: [
//           "nat-8-document-rap-indigenous-participation-plan-endorsement",
//           "nat-8-document-rap-actions-log"
//         ]
//       },
//       {
//         creditCode: "Wfs-2",
//         creditName: "Workforce Training & Skills",
//         levelNotes: {
//           level1: "≥95% of on-site workforce inducted",
//           level2:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual",
//           level3:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual"
//         },
//         levelSensitiveTargetIds: [],
//         levelValues: { level1: 95, level2: null, level3: null },
//         passesWhen:
//           "95% of on-site workforce inducted + skills development evidence",
//         points: 2,
//         setUpFields: ["Training coverage target"],
//         targetIds: [
//           "nat-8-document-workforce-development-strategy",
//           "nat-8-document-sustainability-induction-register"
//         ]
//       },
//       {
//         creditCode: "Wfs-3",
//         creditName: "Diversity & Inclusion",
//         levelNotes: {
//           level1: "Women-in-trades target met (≥4% of trade hours)",
//           level2:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual",
//           level3:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual"
//         },
//         levelSensitiveTargetIds: ["nat-8-hours-women-in-trades"],
//         levelValues: { level1: 4, level2: null, level3: null },
//         passesWhen: "Diversity targets including women in trade roles",
//         points: 1,
//         setUpFields: ["Women-in-trades target"],
//         targetIds: [
//           "nat-8-hours-women-in-trades",
//           "nat-8-document-diversity-inclusion-plan"
//         ]
//       },
//       {
//         creditCode: "Ene-1",
//         creditName: "Energy & Carbon Monitoring",
//         levelNotes: {
//           level1: "Monthly tracking in place",
//           level2:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual",
//           level3:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual"
//         },
//         levelSensitiveTargetIds: [],
//         passesWhen:
//           "Construction energy, fuel & water tracked monthly against reduction targets",
//         points: 1,
//         setUpFields: [],
//         // The report-only energy / fuel / water rows join this credit once
//         // environmental framework targets are re-enabled.
//         targetIds: ["nat-8-document-energy-carbon-management-plan"]
//       },
//       {
//         creditCode: "Rso-6",
//         creditName: "Construction Waste Diversion",
//         levelNotes: {
//           level1: "≥90% diverted from landfill",
//           level2:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual",
//           level3:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual"
//         },
//         levelSensitiveTargetIds: [],
//         levelValues: { level1: 90, level2: null, level3: null },
//         passesWhen:
//           "At least 90% of construction & demolition waste diverted from landfill",
//         points: 2,
//         setUpFields: ["Waste diversion target"],
//         // The 90% threshold itself joins this credit once environmental
//         // targets carry a threshold on the entity.
//         targetIds: ["nat-8-document-waste-management-plan"]
//       },
//       {
//         creditCode: "Sta-2",
//         creditName: "Stakeholder Engagement",
//         levelNotes: {
//           level1: "Engagement program delivered + feedback register maintained",
//           level2:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual",
//           level3:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual"
//         },
//         levelSensitiveTargetIds: [],
//         passesWhen: "Engagement program delivered + community feedback loop",
//         points: 1,
//         setUpFields: [],
//         targetIds: [
//           "nat-8-document-stakeholder-engagement-plan",
//           "nat-8-document-community-feedback-register"
//         ]
//       }
//     ],
//     defaultLevelId: "level1",
//     defaultRating: IS_DEFAULT_RATING,
//     frameworkId: FrameworkId.NAT_8,
//     levels: isRatingLevels,
//     ratings: isRatings
//   },
//   {
//     credits: [
//       {
//         creditCode: "Spr-2",
//         creditName: "Sustainable Procurement Implementation",
//         levelNotes: {
//           level1: "≥1% of operating contract value with verified suppliers",
//           level2: "≥2% of operating contract value (stretch, +2 pts)",
//           level3:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual"
//         },
//         levelSensitiveTargetIds: ["nat-9-spend-verified-supplier"],
//         levelValues: { level1: 1, level2: 2, level3: null },
//         passesWhen:
//           "Spend at least 1% of operating contract value with verified suppliers (2% at Level 2)",
//         points: 3,
//         setUpFields: [
//           "Operating contract value",
//           "Social-spend target %",
//           "Accepted certification bodies"
//         ],
//         targetIds: [
//           "nat-9-spend-verified-supplier",
//           "nat-9-hours-fte-by-group",
//           "nat-9-document-sustainable-procurement-strategy-iso-20400"
//         ]
//       },
//       {
//         creditCode: "Wfs-2",
//         creditName: "Workforce Training & Skills",
//         levelNotes: {
//           level1: "≥70% of operations workforce inducted",
//           level2:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual",
//           level3:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual"
//         },
//         levelSensitiveTargetIds: [],
//         levelValues: { level1: 70, level2: null, level3: null },
//         passesWhen:
//           "70% of operations workforce inducted + ongoing development",
//         points: 2,
//         setUpFields: ["Training coverage target"],
//         targetIds: [
//           "nat-9-document-workforce-development-strategy",
//           "nat-9-document-sustainability-induction-register"
//         ]
//       },
//       {
//         creditCode: "Ene-1",
//         creditName: "Energy & Carbon Monitoring",
//         levelNotes: {
//           level1: "Monthly tracking in place",
//           level2:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual",
//           level3:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual"
//         },
//         levelSensitiveTargetIds: [],
//         passesWhen: "Operational energy, fuel & water tracked monthly",
//         points: 1,
//         setUpFields: [],
//         targetIds: ["nat-9-document-energy-carbon-management-plan"]
//       },
//       {
//         creditCode: "Rso-6",
//         creditName: "Operational Waste Diversion",
//         levelNotes: {
//           level1: "≥80% diverted from landfill",
//           level2:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual",
//           level3:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual"
//         },
//         levelSensitiveTargetIds: [],
//         levelValues: { level1: 80, level2: null, level3: null },
//         passesWhen: "At least 80% of operational waste diverted from landfill",
//         points: 2,
//         setUpFields: ["Waste diversion target"],
//         targetIds: ["nat-9-document-waste-management-plan"]
//       },
//       {
//         creditCode: "Sta-2",
//         creditName: "Stakeholder Engagement",
//         levelNotes: {
//           level1: "Engagement program delivered + feedback register maintained",
//           level2:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual",
//           level3:
//             "Criteria not in the requirements map - confirm against the IS Technical Manual"
//         },
//         levelSensitiveTargetIds: [],
//         passesWhen: "Engagement program delivered + community feedback loop",
//         points: 1,
//         setUpFields: [],
//         targetIds: [
//           "nat-9-document-stakeholder-engagement-plan",
//           "nat-9-document-community-feedback-register"
//         ]
//       }
//     ],
//     defaultLevelId: "level1",
//     defaultRating: IS_DEFAULT_RATING,
//     frameworkId: FrameworkId.NAT_9,
//     levels: isRatingLevels,
//     ratings: isRatings
//   }// ];
